import { useQuery, keepPreviousData as previousPlaceholder } from '@tanstack/react-query';

import Api from '../lib/api/Api';

import { useApi } from '../contexts/ApiContext';

type UseAuthorOptions = Omit<
    Parameters<typeof useQuery<Author | null, Error, Author | null, ['author', string]>>[0],
    'queryKey' | 'queryFn'
> & {
    keepPreviousData?: boolean;
};

export function fetchAuthor(queryClient, api: Api, slug: string): Promise<Author | null> {
    return queryClient.fetchQuery({
        queryKey: ['author', slug],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.authors.findBySlug(queryParam, { signal }),
    });
}

export function fetchAuthorByHandle(queryClient, api: Api, handle: string): Promise<Author | null> {
    return queryClient.fetchQuery({
        queryKey: ['author_handle', handle],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.authors.findByHandle(queryParam, { signal }),
    });
}

export default function useAuthor(slug: string, opts: UseAuthorOptions = null) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const {
        data: author = null,
        isFetched = false,
        ...queryResult
    } = useQuery({
        queryKey: ['author', slug],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.authors.findBySlug(queryParam, { signal }),
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    return {
        author,
        notFound: author === null && isFetched,
        isFetched,
        ...queryResult,
    };
}
